import { format, parseISO } from 'date-fns'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { DateRange } from 'react-day-picker'

import { useIsMobile } from '../../hooks/use-mobile'
import { cn } from '../../utils/cn'
import { Button } from './button'
import { Calendar } from './calendar'
import { Popover, PopoverContent, PopoverTrigger } from './popover'

const toIso = (d: Date) => format(d, 'yyyy-MM-dd')
const fmt = (d: Date) => format(d, 'MMM d, yyyy')
const toRange = (start: string, end: string): DateRange | undefined =>
  start ? { from: parseISO(start), to: end ? parseISO(end) : undefined } : undefined

interface DateRangePickerProps {
  /** Inclusive range bounds as `yyyy-MM-dd` strings; empty shows the placeholder. */
  start: string
  end: string
  onChange: (start: string, end: string) => void
  numberOfMonths?: number
  placeholder?: string
  className?: string
}

/** Generic day-range picker: a single popover with a `Calendar mode="range"`. */
export function DateRangePicker({
  start,
  end,
  onChange,
  numberOfMonths = 2,
  placeholder = 'Pick a range',
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false)
  // Two months is ~600px, wider than a phone; the popover only mounts on open, so there is no flash.
  const isMobile = useIsMobile()
  const [range, setRange] = useState<DateRange | undefined>(() => toRange(start, end))

  // Re-sync when the parent resets the range externally.
  useEffect(() => {
    setRange(toRange(start, end))
  }, [start, end])

  const label = range?.from
    ? range.to
      ? `${fmt(range.from)} – ${fmt(range.to)}`
      : fmt(range.from)
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-10 w-full justify-start text-left font-normal sm:w-72', className)}
        >
          <CalendarIcon className="mr-2 size-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={isMobile ? 1 : numberOfMonths}
          selected={range}
          defaultMonth={range?.from}
          onSelect={(r, clickedDay) => {
            // From an empty range the day picker sets both ends on the first click; take it as the start only.
            if (!range?.from) {
              setRange({ from: clickedDay, to: undefined })
              return
            }
            setRange(r)
            if (r?.from && r.to) {
              onChange(toIso(r.from), toIso(r.to))
              setOpen(false)
            }
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
